import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Activity, MapPin, Lightbulb, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';

const HERO_IMAGE =
  'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/vg6jeiri_CHANGE%20TO%20THIS%20ONE.png';

const reportingOptions = [
  'Manual report compilation',
  'Multiple systems with separate reports',
  'Existing system needs upgrading',
  'No detailed reporting currently',
];

const volumeOptions = [
  'Under €10,000',
  '€10,000 to €50,000',
  'Over €50,000',
  'Multiple locations/varies',
];

const insightOptions = [
  'Real-time performance',
  'Customer behaviour patterns',
  'Location performance',
  'Financial reconciliation',
  'Fraud prevention & risk analysis',
  'All of the above',
];

const accessOptions = [
  'Single location only',
  'Multiple locations',
  'Enterprise access',
  'Franchise/partner reporting',
];

const timelineOptions = [
  'Within 2 weeks',
  'Within a month',
  'Planning ahead',
  'ASAP - current system has issues',
];

const benefits = [
  {
    icon: Activity,
    title: 'Real-Time Intelligence',
    body: 'See your business performance as it happens. Live dashboards, instant alerts and comprehensive analytics that turn transaction data into strategic advantage.',
  },
  {
    icon: MapPin,
    title: 'Multi-Location Mastery',
    body: 'Complete visibility across all sites. Compare performance, track trends and manage multiple locations with unified reporting that scales with your business.',
  },
  {
    icon: Lightbulb,
    title: 'Actionable Insights',
    body: 'Data that drives decisions. From peak hours to customer patterns — get the intelligence you need to optimise operations and maximise profitability.',
  },
];

const Field = ({ label, required, children, testId }) => (
  <div className="mb-7" data-testid={testId}>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} className="accent-[#1F4E79]" />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const Checkbox = ({ value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input type="checkbox" checked={checked} onChange={() => onChange(value)} className="accent-[#1F4E79]" />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const GetStartedTransactionReporting = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessType: '',
    reporting: '',
    volume: '',
    insights: [],
    access: '',
    timeline: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const toggleInsight = (value) =>
    setForm((f) => ({
      ...f,
      insights: f.insights.includes(value) ? f.insights.filter((x) => x !== value) : [...f.insights, value],
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !form.businessType ||
      !form.reporting ||
      !form.volume ||
      !form.access ||
      !form.timeline ||
      !form.name ||
      !form.email ||
      !form.phone
    ) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Thanks! We'll be in touch within 24 hours to discuss your reporting and analytics needs.");
      setForm({
        businessType: '',
        reporting: '',
        volume: '',
        insights: [],
        access: '',
        timeline: '',
        name: '',
        email: '',
        phone: '',
        company: '',
      });
      setSubmitting(false);
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col relative" data-testid="get-started-trx-reporting-page">
      <div className="fixed inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${HERO_IMAGE})` }} aria-hidden="true" />
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1929]/82 via-[#0a1929]/68 to-[#163a5f]/78" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        <div className="h-20" />

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="gstr-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="max-w-4xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              Free Consultation — Tailored Setup
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-4" data-testid="gstr-title">
              Transform Your Data<br />Into Business Growth
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl font-medium">
              Comprehensive reporting and analytics that turn your payment data into actionable insights. Get expert guidance tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form onSubmit={handleSubmit} className="lg:col-span-8 bg-white rounded-xl shadow-2xl p-7 sm:p-9" data-testid="gstr-form">
              <Field label="1. What type of business do you operate?" required testId="gstr-field-business">
                <input
                  type="text"
                  value={form.businessType}
                  onChange={update('businessType')}
                  placeholder="e.g. multi-location retail, restaurant chain, franchise, online marketplace"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                  data-testid="gstr-input-business"
                />
              </Field>

              <Field label="2. What's your current reporting situation?" required testId="gstr-field-reporting">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {reportingOptions.map((opt, i) => (
                    <Radio key={opt} name="reporting" value={opt} checked={form.reporting === opt} onChange={update('reporting')} testId={`gstr-reporting-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="3. What's your monthly transaction volume?" required testId="gstr-field-volume">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {volumeOptions.map((opt, i) => (
                    <Radio key={opt} name="volume" value={opt} checked={form.volume === opt} onChange={update('volume')} testId={`gstr-volume-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="4. Which business insights matter most to you? (Select any that apply)" testId="gstr-field-insights">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {insightOptions.map((opt, i) => (
                    <Checkbox key={opt} value={opt} checked={form.insights.includes(opt)} onChange={toggleInsight} testId={`gstr-insight-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="5. How many locations/users need access?" required testId="gstr-field-access">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {accessOptions.map((opt, i) => (
                    <Radio key={opt} name="access" value={opt} checked={form.access === opt} onChange={update('access')} testId={`gstr-access-${i}`} />
                  ))}
                </div>
              </Field>

              <Field label="6. How soon do you want to implement?" required testId="gstr-field-timeline">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {timelineOptions.map((opt, i) => (
                    <Radio key={opt} name="timeline" value={opt} checked={form.timeline === opt} onChange={update('timeline')} testId={`gstr-timeline-${i}`} />
                  ))}
                </div>
              </Field>

              <div data-testid="gstr-field-contact">
                <label className="block text-sm font-bold text-gray-800 mb-3">7. Contact Details</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={form.name} onChange={update('name')} placeholder="Contact name *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gstr-input-name" />
                  <input type="email" value={form.email} onChange={update('email')} placeholder="Email address *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gstr-input-email" />
                  <input type="tel" value={form.phone} onChange={update('phone')} placeholder="Phone number *" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gstr-input-phone" />
                  <input type="text" value={form.company} onChange={update('company')} placeholder="Business name (optional)" className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none" data-testid="gstr-input-company" />
                </div>
              </div>

              <button type="submit" disabled={submitting} className="group mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed" data-testid="gstr-submit">
                {submitting ? 'Sending…' : 'Request Consultation'}</button>

              <p className="text-xs text-gray-500 mt-4">
                <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />
                Your information is secure and used only to prepare your free reporting consultation.
              </p>
            </form>

            <aside className="lg:col-span-4 space-y-4 lg:pt-16" data-testid="gstr-benefits">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} className="rounded-xl p-5 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-200" data-testid={`gstr-benefit-${i}`}>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Icon size={18} className="text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-tight">{b.title}</h3>
                    </div>
                    <p className="text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </aside>
          </div>
        </main>

        <Footer />
        <BackToTop />
      </div>
    </div>
  );
};

export default GetStartedTransactionReporting;
